import { useRef, useMemo } from 'react'
import { Row, Column } from '@datasets/handsontable/read-grid/cell-renderers/readGridDataCell/types'
import { useDatasetPendingRowsObserver } from '@global/hooks/queries/useDatasetPendingRowsObserver'
import { useDatasetRemovedRowsObserver } from '@global/hooks/queries/useDatasetRemovedRowsObserver'
import { Status, useClassificationRowStatus } from '@global/hooks/useClassificationRowStatus'
import { findInArrays } from '@utils/array'

interface Args {
  rows: Row[]
  columns: Column[]
  headerRow: unknown[]
  datasetId: string
}

/**
 * Some toolbar actions need access to the grid dataset.
 * - A shared hook with both grid and the toolbars.
 */
export const useIndexedData = ({ rows, columns, headerRow, datasetId }: Args) => {
  const dataRef = useRef([])
  const ROW_COUNT_COLUMN_FIELD_ID = -1

  const { pendingRows } = useDatasetPendingRowsObserver({ datasetId })
  const { removedRows } = useDatasetRemovedRowsObserver({ datasetId })

  /**
   * If every row has a classification, we disable the query by default
   * because we don't need to query the classification status.
   */
  const hasClassifications = rows.every(row => row.classification?.summary)

  const { processing, failed } = useClassificationRowStatus({
    datasetId,
    isQueryDefaultEnabled: !hasClassifications,
  })

  const indexData = newRows => {
    const dataRows = newRows.map((row, index) => {
      const classificationRowStatus =
        (findInArrays(
          processing,
          failed,
          classificationStatus => classificationStatus.cvId === row.cvid,
        )?.requestStatus as Status) || Status.UNCLASSIFIED

      return {
        ...row,
        data: {
          ...row?.data,
          [ROW_COUNT_COLUMN_FIELD_ID]: index,
        },
        isDisabled: pendingRows.includes(row?.cvid),
        isRemoving: removedRows.includes(row?.cvid),
        classificationRowStatus,
      }
    })
    const backfillFrom = Number(Object.keys(newRows)[0])

    // Backfill previous loaded data into the new rows,
    // so we are keeping a reference of more than 1000 records;
    // Ideal for copy/pasting the data.
    for (let i = backfillFrom; i > 0; i--) {
      const matchAlreadyLoadedCell = dataRef?.current[i - 1]
      if (!matchAlreadyLoadedCell) break
      dataRows.splice(i - 1, 1, matchAlreadyLoadedCell)
    }

    dataRef.current = dataRows

    return [headerRow, ...dataRows]
  }

  const data = useMemo(() => indexData(rows), [columns, rows, pendingRows, removedRows])

  return {
    data,
  }
}
